import type {
  DocInterpretationLine,
  DocInterpretationReceiptLine,
  DocInterpretationLineStatus,
  DocInterpretationRegion,
  DocInterpretationResult,
} from '../../../../../types/docInterpretation';

/** Linjer gruppert per bestilling, i bestillingsrekkefølge; linjer uten bestilling sist (punkt 14). */
export interface OrderGroup {
  orderNo: number | null;
  lines: DocInterpretationLine[];
}

export function groupLinesByOrder(lines: DocInterpretationLine[]): OrderGroup[] {
  const byOrder = new Map<number | null, DocInterpretationLine[]>();
  for (const line of [...lines].sort((a, b) => a.lineSeq - b.lineSeq)) {
    const key = line.orderNo ?? null;
    const list = byOrder.get(key) ?? [];
    list.push(line);
    byOrder.set(key, list);
  }
  return Array.from(byOrder.entries())
    .sort(([a], [b]) => (a === null ? 1 : b === null ? -1 : a - b))
    .map(([orderNo, groupLines]) => ({ orderNo, lines: groupLines }));
}

/** Leser linjens område på siden. Ugyldig eller manglende område gir null. */
export function parseRegion(regionJson: string | null): DocInterpretationRegion | null {
  if (!regionJson) return null;
  try {
    const r = JSON.parse(regionJson);
    const values = [r.x, r.y, r.width, r.height];
    if (values.some(v => typeof v !== 'number' || Number.isNaN(v))) return null;
    const clamp = (v: number) => Math.min(1, Math.max(0, v));
    const x = clamp(r.x);
    const y = clamp(r.y);
    return { x, y, width: Math.min(clamp(r.width), 1 - x), height: Math.min(clamp(r.height), 1 - y) };
  } catch {
    return null;
  }
}

/** Utheving over linjen på siden, i prosent av siden (for absolutt posisjonering over canvas). */
export function regionStyle(region: DocInterpretationRegion): { left: string; top: string; width: string; height: string } {
  const pct = (v: number) => `${(v * 100).toFixed(2)}%`;
  return { left: pct(region.x), top: pct(region.y), width: pct(region.width), height: pct(region.height) };
}

/** Linjer som krever en avgjørelse før OK (punkt 16–18, 20). */
export function needsDecision(line: DocInterpretationLine): boolean {
  return line.status === 'Decide' || line.status === 'NotFound';
}

/**
 * Kontrollene som vises i oversikten: bare mens linjen venter på en avgjørelse.
 * Batch-usikkerhet har egen status og vises ikke som tekst.
 */
export function openChecks(line: DocInterpretationLine): string[] {
  return needsDecision(line) ? line.checks.filter(c => c !== 'BatchUncertain') : [];
}

/** Antallet som mottas: valgt antall, ellers dokumentets antall (punkt 19). */
export function effectiveQuantity(line: DocInterpretationLine): number | null {
  return line.chosenQuantity ?? line.docQuantity;
}

/** Datodelen (YYYY-MM-DD) av en dato fra API-et, eller null. */
export function dateOnly(value: string | null | undefined): string | null {
  return value ? value.slice(0, 10) : null;
}

/**
 * Mottakslinjene som gjelder: de rettede, ellers én linje med linjens antall og dokumentets batch og
 * best før-dato (punkt 19). Datoene er alltid YYYY-MM-DD.
 */
export function receiptLinesFor(line: DocInterpretationLine): DocInterpretationReceiptLine[] {
  if (line.receiptLines.length > 0) {
    return line.receiptLines.map(r => ({ quantity: r.quantity, batchNo: r.batchNo ?? null, expiryDate: dateOnly(r.expiryDate) }));
  }
  const quantity = effectiveQuantity(line);
  // API-et utelater null-felter; ?? null gir samme form som utkastet, så sammenligningen ikke ser falske endringer
  return quantity == null ? [] : [{ quantity, batchNo: line.docBatchNo ?? null, expiryDate: dateOnly(line.docExpiryDate) }];
}

/** Ett valg innkjøper har gjort på linjen, som oversettelsesnøkkel (under …choices) og verdier. */
export interface LineChoice {
  key: 'replace' | 'linked' | 'newOrderLine' | 'quantityDocument' | 'quantityRemaining' | 'quantityCustom';
  params: Record<string, string | number>;
}

/**
 * Valgene innkjøper har gjort på linjen, så de kan kontrolleres i oversikten før OK — OK kan ikke angres.
 * Holdt utenfor viser ingen valg; statusmerket sier det.
 */
export function lineChoices(line: DocInterpretationLine): LineChoice[] {
  if (line.excluded) return [];
  const choices: LineChoice[] = [];
  if (line.notFoundChoice === 'NewOrderLine' && line.orderNo != null) {
    choices.push({ key: 'newOrderLine', params: { '0': line.orderNoERP || line.orderNo, '1': line.itemNo ?? '' } });
  } else if (line.matchMethod === 'Manual' && line.orderNo != null) {
    choices.push({ key: 'linked', params: { '0': line.orderNoERP || line.orderNo, '1': line.orderLineNo ?? '' } });
  }
  if (line.conflictChoice === 'Replace') choices.push({ key: 'replace', params: {} });
  const quantity = effectiveQuantity(line) ?? '';
  if (line.quantityChoice === 'Document') choices.push({ key: 'quantityDocument', params: { '0': quantity } });
  if (line.quantityChoice === 'Remaining') choices.push({ key: 'quantityRemaining', params: { '0': quantity } });
  if (line.quantityChoice === 'Custom') choices.push({ key: 'quantityCustom', params: { '0': quantity } });
  return choices;
}

/** Oppsummering i bekreftelsen ved OK: linjer med, holdt utenfor og antall som tas med. */
export function approvalSummary(result: DocInterpretationResult): { included: number; excluded: number; quantity: number; newOrderLines: number; serialLines: number } {
  const included = result.lines.filter(l => !l.excluded && l.orderNo != null);
  return {
    included: included.length,
    excluded: result.lines.length - included.length,
    quantity: included.reduce((sum, l) => sum + (effectiveQuantity(l) ?? 0), 0),
    // OK endrer innkjøpsordren: disse linjene opprettes
    newOrderLines: included.filter(l => l.notFoundChoice === 'NewOrderLine' && l.orderLineNo == null).length,
    // Serienummervarer ankomstregistreres ikke ved OK
    serialLines: included.filter(l => l.serialItem).length,
  };
}

/** OK kan trykkes direkte fra oversikten når alt er avgjort (punkt 14 og 20). */
export function canApprove(result: DocInterpretationResult | null): boolean {
  return !!result && result.status === 'Interpreted' && result.allDecided && approvalSummary(result).included > 0;
}

/** Fargeklasser per status. Klassene har overstyringer for grå og mørk modus i index.css. */
export function statusClasses(status: DocInterpretationLineStatus): string {
  switch (status) {
    case 'Ready':
      return 'bg-green-50 text-green-800 border-green-200';
    case 'Decide':
      return 'bg-amber-50 text-amber-800 border-amber-200';
    case 'NotFound':
      return 'bg-red-50 text-red-800 border-red-200';
    case 'BatchUncertain':
      return 'bg-purple-50 text-purple-800 border-purple-200';
    case 'Excluded':
    default:
      return 'bg-gray-100 text-gray-500 border-gray-200';
  }
}
